import JSZip from 'jszip';
import { BufferGeometry, Vector3 } from 'three';

const encoder = new TextEncoder();

function number(value: number) {
  return Number.isFinite(value) ? value.toFixed(5).replace(/\.?(0+)$/, '') || '0' : '0';
}

function triangleVertices(geometry: BufferGeometry, visit: (a: Vector3, b: Vector3, c: Vector3) => void) {
  const position = geometry.getAttribute('position');
  const index = geometry.getIndex();
  const a = new Vector3();
  const b = new Vector3();
  const c = new Vector3();
  const count = index ? index.count : position.count;
  for (let i = 0; i < count; i += 3) {
    const ai = index ? index.getX(i) : i;
    const bi = index ? index.getX(i + 1) : i + 1;
    const ci = index ? index.getX(i + 2) : i + 2;
    a.fromBufferAttribute(position, ai);
    b.fromBufferAttribute(position, bi);
    c.fromBufferAttribute(position, ci);
    visit(a, b, c);
  }
}

export function createStl(geometry: BufferGeometry, onProgress: (percent: number) => void = () => {}) {
  const count = (geometry.getIndex()?.count ?? geometry.getAttribute('position').count) / 3;
  const output = new ArrayBuffer(84 + count * 50);
  const view = new DataView(output);
  new Uint8Array(output, 0, 80).set(encoder.encode('cloisonne-studio binary STL; units: millimeter'));
  view.setUint32(80, count, true);
  let offset = 84;
  let triangle = 0;
  const normal = new Vector3();
  const ab = new Vector3();
  const ac = new Vector3();
  triangleVertices(geometry, (a, b, c) => {
    normal.copy(ab.subVectors(b, a)).cross(ac.subVectors(c, a)).normalize();
    for (const point of [normal, a, b, c]) {
      view.setFloat32(offset, point.x, true);
      view.setFloat32(offset + 4, point.y, true);
      view.setFloat32(offset + 8, point.z, true);
      offset += 12;
    }
    view.setUint16(offset, 0, true);
    offset += 2;
    if (++triangle % 16384 === 0) onProgress(triangle / count * 100);
  });
  onProgress(100);
  return output;
}

function meshXml(geometry: BufferGeometry, onProgress: (percent: number) => void) {
  const position = geometry.getAttribute('position');
  const index = geometry.getIndex();
  let vertices = '';
  for (let i = 0; i < position.count; i += 1) {
    vertices += `<vertex x="${number(position.getX(i))}" y="${number(position.getY(i))}" z="${number(position.getZ(i))}"/>`;
    if (i % 16384 === 0) onProgress(i / position.count * 50);
  }
  let triangles = '';
  const count = index ? index.count : position.count;
  for (let i = 0; i < count; i += 3) {
    const a = index ? index.getX(i) : i;
    const b = index ? index.getX(i + 1) : i + 1;
    const c = index ? index.getX(i + 2) : i + 2;
    triangles += `<triangle v1="${a}" v2="${b}" v3="${c}"/>`;
    if (i % 49152 === 0) onProgress(50 + i / count * 50);
  }
  onProgress(100);
  return `<mesh><vertices>${vertices}</vertices><triangles>${triangles}</triangles></mesh>`;
}

export async function create3mf(wireGeometry: BufferGeometry, baseGeometry: BufferGeometry | null, onProgress: (percent: number) => void = () => {}) {
  const objects: Array<{ id: number; name: string; geometry: BufferGeometry }> = [];
  if (baseGeometry) objects.push({ id: 1, name: '底板', geometry: baseGeometry });
  objects.push({ id: baseGeometry ? 2 : 1, name: '掐丝线条', geometry: wireGeometry });
  const model = `<?xml version="1.0" encoding="UTF-8"?><model unit="millimeter" xml:lang="zh-CN" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02"><metadata name="Title">掐丝工坊导出</metadata><metadata name="Application">掐丝工坊</metadata><resources>${objects.map((part, i) => `<object id="${part.id}" type="model" name="${part.name}">${meshXml(part.geometry, (percent) => onProgress((i + percent / 100) / objects.length * 60))}</object>`).join('')}</resources><build>${objects.map((part) => `<item objectid="${part.id}"/>`).join('')}</build></model>`;
  const zip = new JSZip();
  zip.file('[Content_Types].xml', '<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/></Types>');
  zip.folder('_rels')?.file('.rels', '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/></Relationships>');
  zip.folder('3D')?.file('3dmodel.model', model);
  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } }, (metadata) => onProgress(60 + metadata.percent * 0.4));
}

export function download(blob: Blob, filename: string, link: HTMLAnchorElement) {
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.textContent = `点击保存 ${filename}（${blob.size < 1048576 ? (blob.size / 1024).toFixed(1) + ' KB' : (blob.size / 1048576).toFixed(1) + ' MB'}）`;
  link.hidden = false;
  link.click();
  // Keep the URL available for delayed browser downloads and explicit user retries.
  const url = link.href;
  return () => { URL.revokeObjectURL(url); link.removeAttribute('href'); link.hidden = true; };
}

export function utf8(content: string) {
  return encoder.encode(content);
}
